import { Fragment } from "react";
import {
  ClaudiaReferenceLink,
  getClaudiaReferenceInternalPath,
} from "@/components/ui/claudia-reference-link";
import { renderMessageMentionLink } from "@/components/ui/message-markdown-mention";
import {
  useOptionalMentionInteraction,
  useReferencePresentationResolver,
} from "@/features/rich-text/mentions";
import { parseMentionVersionRef } from "@/lib/message-mention-link";
import { useProjectNavigate } from "../routing/project-route-context";
import type { AssistantLinkedTextPart } from "../types";

interface AssistantLinkedTextProps {
  parts?: AssistantLinkedTextPart[] | null;
  fallback: string;
}

/**
 * Renders an activity label's typed runs. A `mention` part is the same chip the chat
 * transcript renders — same icon, same activation, same version facet — so a reference reads
 * identically whether the assistant wrote it in a message or in an activity header.
 */
export function AssistantLinkedText({ parts, fallback }: AssistantLinkedTextProps) {
  const navigate = useProjectNavigate();
  const mentionInteraction = useOptionalMentionInteraction();
  const resolveReferencePresentation = useReferencePresentationResolver();

  if (!parts?.length) {
    return <>{fallback}</>;
  }

  return (
    <>
      {parts.map((part, index) => {
        if (part.kind === "text") {
          return <span key={`text:${index}:${part.text}`}>{part.text}</span>;
        }
        if (part.kind === "mention") {
          return (
            <Fragment key={`mention:${index}:${part.mentionType}:${part.mentionId}`}>
              {renderMessageMentionLink({
                children: part.text,
                mention: {
                  id: part.mentionId,
                  mentionType: part.mentionType,
                  versionIntent: parseMentionVersionRef(part.versionRef),
                },
                mentionInteraction,
                resolveReferencePresentation,
              })}
            </Fragment>
          );
        }
        if (!getClaudiaReferenceInternalPath(part.href)) {
          return <span key={`unsafe:${index}:${part.href}:${part.text}`}>{part.text}</span>;
        }
        return (
          <ClaudiaReferenceLink
            key={`link:${index}:${part.href}:${part.text}`}
            href={part.href}
            targetKind={part.targetKind}
            entityTypeKey={part.entityTypeKey}
            entityId={part.entityId}
            versionNumber={part.versionNumber}
            onNavigate={navigate}
          >
            {part.text}
          </ClaudiaReferenceLink>
        );
      })}
    </>
  );
}
